import React, { useState, useEffect, useRef } from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import "./App.css"; // External CSS for styling

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [typing, setTyping] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [msalInstance, setMsalInstance] = useState(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/variables");
        const config = await response.json();

        const msalConfig = {
          auth: {
            clientId: config.clientId,
            authority: "https://login.microsoftonline.com/common",
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: "sessionStorage",
            storeAuthStateInCookie: false,
          },
        };

        const instance = new PublicClientApplication(msalConfig);
        setMsalInstance(instance);

        const account = instance.getAllAccounts()[0];
        if (account) {
          setIsAuthenticated(true);
          setUserEmail(account.username);
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      }
    };

    fetchConfig();
  }, []);

  const login = async () => {
    if (!msalInstance) return;
    try {
      const loginResponse = await msalInstance.loginPopup({ scopes: ["User.Read"] });
      setUserEmail(loginResponse.account.username);
      setIsAuthenticated(true);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const logout = () => {
    if (msalInstance) {
      msalInstance.logoutPopup();
      setIsAuthenticated(false);
      setUserEmail(null);
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) return;
    const newMessages = [...messages, { text: message, sender: "user", timestamp: new Date().toLocaleTimeString() }];
    setMessages(newMessages);
    setMessage("");
    setTyping(true);

    try {
      const response = await fetch("/api/openai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, email: userEmail }),
      });

      const data = await response.json();
      setMessages((prev) => [...prev, { text: data.reply, sender: "bot", timestamp: new Date().toLocaleTimeString() }]);
    } catch (error) {
      setMessages((prev) => [...prev, { text: "Error occurred.", sender: "bot", timestamp: new Date().toLocaleTimeString() }]);
    } finally {
      setTyping(false);
    }
  };

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  return (
    <div className="chatbot-container">
      <div className="login-section">
        {isAuthenticated ? (
          <>
            <span>Logged in as: {userEmail}</span>
            <button onClick={logout} className="logout-btn">Logout</button>
          </>
        ) : (
          <button onClick={login} className="login-btn">Login with Azure</button>
        )}
      </div>

      <div className="chat-container">
        <div className="chat-messages" ref={chatContainerRef}>
          {messages.map((msg, index) => (
            <div key={index} className={`message ${msg.sender === "user" ? "user-message" : "bot-message"}`}>
              {msg.text}
              <div className="timestamp">{msg.timestamp}</div>
            </div>
          ))}
          {typing && <div className="typing-indicator">AI is typing...</div>}
        </div>

        <div className="input-container">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && sendMessage()}
            placeholder="Type your message..."
          />
          <button onClick={sendMessage} disabled={!message.trim()}>Send</button>
        </div>
      </div>
    </div>
  );
};

export default ChatBot;
